/**
 * Shop owner-address parsing / normalization.
 *
 * Sui addresses are hex (case-insensitive); on-chain SDK output and Supabase
 * storage are lowercase. Inputs from QR scans, paste, or hand-typed URLs may
 * arrive uppercase or with surrounding whitespace — normalize before lookup
 * so strict `.eq('owner_address', ...)` queries don't miss.
 */

export function normalizeAddress(raw: string): string {
  const trimmed = raw.trim();
  if (/^0[xX][0-9a-fA-F]+$/.test(trimmed)) return trimmed.toLowerCase();
  return trimmed;
}

/**
 * Case-insensitive, whitespace-tolerant address equality. Use this for EVERY
 * shop-owner-address join instead of raw `===` — on-chain SDK output, Supabase
 * storage, URL params, and pasted strings can differ only by case/whitespace,
 * and a raw `===` miss is what silently produces a "shop not found" / address-
 * as-name fallback. Returns false (never throws) if either side is missing.
 */
export function addressesEqual(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false;
  return normalizeAddress(a) === normalizeAddress(b);
}

// A name that is actually an address-derived placeholder the old code persisted
// into shops.name (e.g. "Shop 0x330069..."). Defensively rejected at render so
// stale rows never show an address as a name, even before the DB is cleaned.
const PLACEHOLDER_NAME_RE = /^Shop\s+0x[0-9a-fA-F]{4,}/;

/**
 * The single source of truth for displaying a shop's name. Returns the trimmed
 * name, or 'Unnamed Shop' when the name is missing, blank, or a legacy address
 * placeholder. NEVER returns an address — that is the whole point.
 */
export function resolveShopName(shop: { name?: string | null } | null | undefined): string {
  const name = shop?.name?.trim();
  if (!name || PLACEHOLDER_NAME_RE.test(name)) return 'Unnamed Shop';
  return name;
}

/**
 * Pull a shop owner address out of:
 *   - a full URL with `?shop=<addr>`
 *   - a partial URL-ish string containing `?shop=` or `&shop=`
 *   - a raw `0x...` address
 *
 * Returns the normalized lowercase address, or null if no recognizable
 * address can be found.
 */
export function extractShopAddress(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    const shopParam = url.searchParams.get('shop');
    if (shopParam) return normalizeAddress(shopParam);
  } catch { /* not a URL */ }

  const match = trimmed.match(/[?&]shop=([^&\s]+)/);
  if (match) return normalizeAddress(match[1]);

  if (trimmed.startsWith('0x') || trimmed.startsWith('0X')) {
    return normalizeAddress(trimmed);
  }

  return null;
}

export function shortAddress(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-4)}`;
}
